import { FC, useState } from "react";
import { Check, MessageSquare, Image as ImageIcon, Video as VideoIcon, Phone as PhoneIcon, ArrowLeft, MoreVertical, Smile, Paperclip, Camera, Mic, File, Copy, Reply, SquareArrowOutUpRight, List } from "lucide-react";
import { AUTH_BODY_TRANSLATIONS } from "@/features/whats-app-crm/utils/language";
import { inferVariableTypeFromTemplate } from "@/features/whats-app-crm/utils/transformTemplate";
import { cn } from "@/utils/cn";
import DOMPurify from "dompurify";

// ─── Types ────────────────────────────────────────────────────────────────────
export type Category = "UTILITY" | "MARKETING" | "AUTHENTICATION";
export type HeaderType = "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT" | "LOCATION";
export type ButtonType = "quick_reply" | "url" | "phone_number" | "otp" | "copy_code" | "flow" | "voice_call";

export interface HeaderState {
    type: HeaderType;
    text: string;
    mediaExample: string;
    mediaPreview: string;
    example: string;
    fileName?: string;
    fileSize?: string;
    pageCount?: number;
}
export interface BodyState {
    text: string;
    examples: string[];
}
export interface FooterState {
    text: string;
    addSecurityRecommendation?: boolean;
    codeExpirationMinutes?: number | string;
}
export interface ButtonItem {
    id: number;
    type: ButtonType;
    text: string;
    autoFocus?: boolean;
    url?: string;
    urlExample?: string;
    phone?: string;
    flowId?: string;
    flowAction?: string;
    copyCodeExample?: string;
    otpType?: "copy_code" | "one_tap";
    voiceCallDisplayText?: string;
    voiceCallTtl?: number;
    voiceCallPayload?: string;
}

export interface FormValues {
    templateName: string;
    category: Category;
    language: string;
    variableType: "positional" | "named";
    header: HeaderState;
    body: BodyState;
    footer: FooterState;
    buttons: ButtonItem[];
    templateLibraryIndustryIds?: string[];
    templateLibrarySubcategoryIds?: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────
export const CATEGORY_META: Record<Category, { hint: string; color: string }> = {
    UTILITY: { hint: "Order updates, shipping, account notifications", color: "text-blue-400" },
    MARKETING: { hint: "Promotions, offers, announcements", color: "text-purple-400" },
    AUTHENTICATION: { hint: "OTP codes only — restricted format", color: "text-amber-400" },
};

function truncateFileName(fileName: string, maxLen: number = 30): string {
    if (!fileName || fileName.length <= maxLen) return fileName;
    const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
    const nameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;

    const availableForName = maxLen - (extension ? extension.length + 4 : 3);
    if (availableForName <= 0) return fileName.substring(0, maxLen - 3) + "...";

    const prefixLen = Math.ceil(availableForName / 2);
    const suffixLen = Math.floor(availableForName / 2);

    const prefix = nameWithoutExt.substring(0, prefixLen);
    const suffix = nameWithoutExt.substring(nameWithoutExt.length - suffixLen);

    return `${prefix}...${suffix}${extension ? '.' + extension : ''}`;
}



function getAuthBodyText(language: string, withSecurity: boolean): string {
    const translations = AUTH_BODY_TRANSLATIONS[language] || AUTH_BODY_TRANSLATIONS["en"];
    return withSecurity ? translations.withSecurity : translations.default;
}

// ─── Mapper/Normalization ─────────────────────────────────────────────────────
/**
 * Normalizes different WhatsApp template data structures into a unified FormValues shape.
 */
function normalizeValues(v: any): FormValues | null {
    if (!v) return null;

    // Check if it's already in the target shape (CRM model)
    if (v.header && typeof v.header === 'object' && !Array.isArray(v.header) && 'type' in v.header && 'text' in v.header && v.body && typeof v.body === 'object' && !Array.isArray(v.body) && 'text' in v.body) {
        return {
            ...v,
            header: {
                ...v.header,
                type: v.header.type?.toUpperCase(),
                mediaPreview: v.header.mediaPreview || v.header.mediaUrl || "",
                fileName: v.header.fileName || v.header.filename || v.fileName || v.filename || ""
            }
        } as FormValues;
    }

    // Handle Backend Template object (RawTemplate)
    if (v.metaPayload || v._id) {
        const payload = v.metaPayload || v;
        const comps = payload.components || [];
        const hd = comps.find((c: any) => c.type?.toLowerCase() === 'header');
        const bd = comps.find((c: any) => c.type?.toLowerCase() === 'body');
        const ft = comps.find((c: any) => c.type?.toLowerCase() === 'footer');
        const bt = comps.find((c: any) => c.type?.toLowerCase() === 'buttons');

        const headerExampleFromText = hd?.example?.header_text;
        let headerExampleValue = "";
        if (headerExampleFromText !== undefined) {
            if (Array.isArray(headerExampleFromText[0])) {
                headerExampleValue = headerExampleFromText[0][0] || headerExampleFromText[0] || "";
            } else {
                headerExampleValue = headerExampleFromText[0] || "";
            }
        }
        return {
            templateName: v.metaTemplateName || payload.name || "",
            category: (v.category?.toUpperCase() as Category) || "MARKETING",
            language: v.language || payload.language || "en_US",
            variableType: inferVariableTypeFromTemplate(payload),
            header: {
                type: (v.header?.type || hd?.format?.toUpperCase() as HeaderType) || "TEXT",
                text: v.header?.text || hd?.text || "",
                mediaExample: v.header?.mediaExample || hd?.example?.header_handle?.[0] || headerExampleValue || "",
                mediaPreview: v.header?.mediaPreview || v.mediaUrl || hd?.mediaUrl || "",
                example: headerExampleValue || hd?.example?.header_handle?.[0] || "",
                fileName: v.header?.fileName || v.header?.filename || v.fileName || v.filename || hd?.fileName || hd?.filename || "",
                fileSize: v.header?.fileSize || v.header?.file_size || v.fileSize || hd?.fileSize || "",
                pageCount: v.header?.pageCount || v.header?.page_count || v.pageCount || hd?.pageCount || undefined,
            },
            body: {
                text: (typeof v.body === 'string' ? v.body : bd?.text) || (payload.category?.toLowerCase?.() === 'authentication' ? getAuthBodyText(v.language || payload.language || 'en_US', Boolean(bd?.add_security_recommendation)) : ""),
                examples: bd?.example?.body_text?.[0] || []
            },
            footer: {
                text: ft?.text || "",
                addSecurityRecommendation: Boolean(bd?.add_security_recommendation),
                codeExpirationMinutes: ft?.code_expiration_minutes,
            },
            buttons: bt?.buttons?.map((b: any, i: number) => ({
                id: i,
                type: (b.type === 'PHONE_NUMBER' ? 'phone_number' : b.type?.toLowerCase() as ButtonType),
                text: b.text || b.label || b.title || (b.type === 'OTP' ? 'Copy code' : ""),
                url: b.url || b.value,
                phone: b.phone_number || b.value,
            })) || []
        };
    }

    // Handle Chatbot Builder model (TemplateFormValues)
    if ('headerType' in v || 'headerText' in v) {
        return {
            templateName: v.name || v.metaTemplateName || "New Template",
            category: (v.category?.toUpperCase() as Category) || "MARKETING",
            language: v.language || "en_US",
            variableType: v.parameterFormat === "NAMED" ? "named" : "positional",
            header: {
                type: (v.headerType?.toUpperCase() === 'NONE' ? 'TEXT' : v.headerType?.toUpperCase() as HeaderType) || "TEXT",
                text: v.headerText || "",
                mediaExample: v.header?.image?.mediaHandle || v.header?.video?.mediaHandle || v.header?.document?.mediaHandle || "",
                mediaPreview: v.header?.image?.mediaUrl || v.header?.video?.mediaUrl || v.header?.document?.mediaUrl || "",
                example: "",
                fileName: v.header?.document?.fileName || v.header?.document?.filename || v.header?.fileName || v.header?.filename || v.fileName || v.filename || "",
                fileSize: v.header?.document?.fileSize || v.header?.fileSize || v.fileSize || "",
                pageCount: v.header?.document?.pageCount || v.header?.pageCount || v.pageCount || undefined,
            },
            body: {
                text: v.body || "",
                examples: v.variables?.map((va: any) => va.fallback) || []
            },
            footer: {
                text: v.footer || ""
            },
            buttons: v.buttons?.map((b: any, i: number) => ({
                id: i,
                type: b.type === 'phone' ? 'phone_number' : (b.type === 'whatsapp_call' ? 'voice_call' : b.type as ButtonType),
                text: b.text || "",
                url: b.type === 'url' ? b.value : undefined,
                phone: b.type === 'phone' ? b.value : undefined,
            })) || []
        };
    }

    return null;
}

// ─── Components ─────────────────────────────────────────────────────────────

// Helper function to replace variables with example values in preview
function replaceVariablesWithExamples(text: string, variableType: "positional" | "named", examples: string[]): string {
    if (variableType === "positional") {
        // Replace {{1}}, {{2}}, etc. with example values
        return text.replace(/\{\{(\d+)\}\}/g, (match, num) => {
            const index = parseInt(num) - 1;
            return examples[index] || match;
        });
    } else {
        // Replace {{variable_name}} with example values
        return text.replace(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, (match, varName) => {
            // For named variables, we need to find the index in the examples array
            // Extract all named variables from the text in order
            const allNamedVars: string[] = [];
            text.replace(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, (_, name) => {
                allNamedVars.push(name);
                return _;
            });
            const index = allNamedVars.indexOf(varName);
            return index >= 0 && examples[index] ? examples[index] : match;
        });
    }
}

export const PhonePreview: FC<{ s: FormValues }> = ({ s }) => {
    // Replace variables in header and body text with example values for preview
    const headerTextWithValues = s.header.text
        ? replaceVariablesWithExamples(s.header.text, s.variableType, s.header.example ? [s.header.example] : [])
        : s.header.text;
    const bodyTextWithValues = s.body.text
        ? replaceVariablesWithExamples(s.body.text, s.variableType, s.body.examples || [])
        : s.body.text;

    const render = (text = "") => {
        const html = text
            .replace(/&/g, "&amp;").replace(/</g, "&lt;")
            .replace(/\*(.+?)\*/g, "<strong>$1</strong>")
            .replace(/_(.+?)_/g, "<em>$1</em>")
            .replace(/~(.+?)~/g, "<del>$1</del>")
            .replace(/`(.+?)`/g, '<code class="bg-black/5 dark:bg-black/20 px-1 rounded text-xs font-mono">$1</code>')
            .replace(/\{\{([^}]+)\}\}/g, ' {{$1}} ')
            .replace(/\n/g, "<br/>");
        const sanitizedHtml = DOMPurify.sanitize(html);
        return <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
    };

    const empty = !s.body.text && s.buttons.length === 0 && !s.header.text && !s.header.mediaExample;
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();

    // 👉 WhatsApp logic
    const showAllDirectly = s.buttons.length <= 3;

    const visibleButtons = showAllDirectly
        ? s.buttons
        : s.buttons.slice(0, 2);

    const hasMore = s.buttons.length > 3;

    // 👉 Popup state
    const [showCtaPopup, setShowCtaPopup] = useState(false);


    return (
        <div className="relative w-full max-w-[520px] mx-auto p-4 select-none group/phone h-full flex flex-col items-center justify-center min-h-[75vh]">
            <div className="relative w-full h-full overflow-hidden rounded-3xl">
                {/* Phone Frame - Pro Look */}
                <div className="bg-[#0b0b0b] rounded-4xl p-3 shadow-2xl  w-full h-full">

                    {/* Speaker/Camera hole */}
                    <div className="absolute top-5 left-1/2 -translate-x-1/2 w-4 h-4 bg-black rounded-full z-50 border-2 border-gray-800" />

                    {/* Screen Container */}
                    <div className="bg-[#E5DDD5] dark:bg-[#0B141A] rounded-3xl h-full overflow-hidden flex flex-col relative shadow-inner">
                        <div className="bg-[#008069]">
                            {/* Status Bar */}
                            <div className="h-8 flex items-center justify-between px-8 bg-transparent z-30">
                                <span className="text-[12px] font-bold text-gray-900 dark:text-white/90">{time.split(' ')[0]}</span>
                                <div className="flex items-center gap-1.5 opacity-80">
                                    <svg className="w-4 h-4 fill-current text-gray-900 dark:text-white" viewBox="0 0 24 24"><path d="M12.01 2.14c-4.43 0-8.24 2.15-10.51 5.48l10.51 13.56 10.51-13.56c-2.27-3.33-6.08-5.48-10.51-5.48z" /></svg>
                                    <svg className="w-4 h-4 fill-current text-gray-900 dark:text-white" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16a6.471 6.471 0 0 0 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zM9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
                                    <div className="w-6 h-3.5 border-2 border-gray-400 dark:border-white/30 rounded-xs relative">
                                        <div className="absolute inset-0.5 bg-gray-900 dark:bg-white/80 w-[80%]" />
                                    </div>
                                </div>
                            </div>

                            {/* App Header - Authentic WhatsApp Business */}
                            <div className="h-16 flex items-center justify-between px-3 z-30 shadow-md">
                                <div className="flex items-center gap-1">
                                    <ArrowLeft size={18} className=" text-white" />
                                    <div className="min-w-8 w-8 h-8 rounded-full bg-green-600 flex items-center justify-center overflow-hidden mr-1 p-1.5">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            shapeRendering="geometricPrecision"
                                            textRendering="geometricPrecision"
                                            imageRendering="optimizeQuality"
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            viewBox="0 0 509 511.514"
                                        >
                                            <path
                                                fill="white"
                                                d="M434.762 74.334C387.553 26.81 323.245 0 256.236 0h-.768C115.795.001 2.121 113.696 2.121 253.456l.001.015a253.516 253.516 0 0033.942 126.671L0 511.514l134.373-35.269a253.416 253.416 0 00121.052 30.9h.003.053C395.472 507.145 509 393.616 509 253.626c0-67.225-26.742-131.727-74.252-179.237l.014-.055zM255.555 464.453c-37.753 0-74.861-10.22-107.293-29.479l-7.72-4.602-79.741 20.889 21.207-77.726-4.984-7.975c-21.147-33.606-32.415-72.584-32.415-112.308 0-116.371 94.372-210.743 210.741-210.743 56.011 0 109.758 22.307 149.277 61.98a210.93 210.93 0 0161.744 149.095c0 116.44-94.403 210.869-210.844 210.869h.028zm115.583-157.914c-6.363-3.202-37.474-18.472-43.243-20.593-5.769-2.121-10.01-3.202-14.315 3.203-4.305 6.404-16.373 20.593-20.063 24.855-3.69 4.263-7.401 4.815-13.679 1.612-6.278-3.202-26.786-9.883-50.899-31.472a192.748 192.748 0 01-35.411-43.867c-3.712-6.363-.404-9.777 2.82-12.873 3.224-3.096 6.363-7.381 9.48-11.092a41.58 41.58 0 006.357-10.597 11.678 11.678 0 00-.508-11.09c-1.718-3.18-14.444-34.357-19.534-47.06-5.09-12.703-10.37-10.603-14.272-10.901-3.902-.297-7.911-.19-12.089-.19a23.322 23.322 0 00-16.964 7.911c-5.707 6.298-22.1 21.673-22.1 52.849s22.671 61.249 25.852 65.532c3.182 4.284 44.663 68.227 108.288 95.649 15.099 6.489 26.891 10.392 36.053 13.403a87.504 87.504 0 0025.216 3.718c4.905 0 9.82-.416 14.65-1.237 12.174-1.782 37.453-15.291 42.776-30.073s5.303-27.57 3.711-30.093c-1.591-2.524-5.704-4.369-12.088-7.615l-.038.021z"
                                            />
                                        </svg>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-white font-bold leading-tight text-sm">WhatsApp Business</span>
                                        <span className="text-[12px] text-white/70 font-medium">online</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 text-white">
                                    <MoreVertical size={18} />
                                </div>
                            </div>
                        </div>
                        {/* Chat Area - Doodle Background */}
                        <div
                            className="flex-1 flex flex-col p-3 relative overflow-y-auto custom-scrollbar h-[500px]
                            bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')]
                            dark:bg-[url('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSeK45TVUrFJDhUrSJJEdlEOwHlZergU0vIZg&s')]
                            bg-[#E5DDD5] dark:bg-[#0B141A]
                        "
                            style={{
                                backgroundSize: "400px",
                                backgroundRepeat: "repeat",
                            }}
                        >

                            <div className="text-center mb-6 relative z-10">
                                <span className="px-3 py-1 bg-white/50 dark:bg-white/5 text-[11px] text-gray-500 dark:text-white/40 rounded-lg shadow-xs border border-black/5 dark:border-white/5 font-bold uppercase tracking-widest">Today</span>
                            </div>

                            {empty ? (
                                <div className="flex-1 flex items-center justify-center text-center px-8 relative z-10 opacity-30">
                                    <div className="space-y-4">
                                        <MessageSquare className="w-12 h-12 mx-auto dark:text-white" />
                                        <p className="text-sm dark:text-white">No content to preview</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="relative z-10 flex flex-col gap-4">
                                    {/* Message Bubble Container */}
                                    <div className="w-[90%] max-w-[90%] h-[200px] mr-auto relative group animate-in slide-in-from-left-2 fade-in duration-300">
                                        {/* Bubble Tail */}
                                        <div className="absolute -left-2 top-0 w-3 h-3 bg-white dark:bg-[#1F2C33]"
                                            style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />

                                        <div className="bg-white dark:bg-[#1F2C33] rounded-md p-0.5 rounded-tl-none">
                                            {/* Header Color Accent (Conditional for Template types) */}
                                            {headerTextWithValues && headerTextWithValues.includes("Confirmed") && (
                                                <div className="bg-[#008069] px-3 py-2 flex items-center gap-2 mb-1">
                                                    <div className="p-1 bg-white/20 rounded">
                                                        <Check className="w-4 h-4 text-white" />
                                                    </div>
                                                    <span className="text-white font-bold text-sm tracking-tight">{render(headerTextWithValues)}</span>
                                                </div>
                                            )}

                                            {/* Header Media */}
                                            {s.header.type == "IMAGE" && (
                                                <div className={cn("relative bg-black/5 dark:bg-black/20 m-0.5 rounded-sm overflow-hidden", !s.header.mediaPreview && "aspect-video")}>
                                                    {s.header.mediaPreview ? (
                                                        <img src={s.header.mediaPreview} alt="" className="w-full h-auto object-contain" />
                                                    ) : (
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center opacity-20 gap-1">
                                                            <ImageIcon size={40} />
                                                            <span className="text-[10px] font-black dark:text-white">{s.header.type}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {s.header.type == "VIDEO" && (
                                                <div className="relative aspect-video bg-black/5 dark:bg-black/20 m-0.5 rounded-sm overflow-hidden">
                                                    {s.header.mediaPreview ? (
                                                        <video
                                                            className="w-full h-full object-cover"
                                                            controls
                                                        >
                                                            <source src={s.header.mediaPreview} type="video/mp4" />
                                                            Your browser does not support the video tag.
                                                        </video>
                                                    ) : (
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center opacity-20 gap-1">
                                                            {s.header.type === "VIDEO" && <VideoIcon size={40} />}
                                                            <span className="text-[10px] font-black dark:text-white">{s.header.type}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {s.header.type == "DOCUMENT" && (() => {
                                                const hasDocument =
                                                    Boolean(s.header.mediaPreview) ||
                                                    Boolean(s.header.example) ||
                                                    Boolean(s.header.fileName);
                                                if (!hasDocument) {
                                                    return (
                                                        <div className="relative aspect-video bg-black/5 dark:bg-black/20 m-0.5 rounded-sm overflow-hidden">
                                                            <div className="absolute inset-0 flex flex-col items-center justify-center opacity-20 gap-1">
                                                                <File size={40} />
                                                                <span className="text-[10px] font-black dark:text-white">{s.header.type}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                const cleanFileNameFromUrl = (url?: string) => {
                                                    if (!url || url.startsWith('blob:') || url.startsWith('data:')) return "";
                                                    const name = url.split('?')[0].split('#')[0].split('/').pop();
                                                    return name && name.includes('.') ? decodeURIComponent(name) : "";
                                                };

                                                const filename =
                                                    s.header.fileName ||
                                                    cleanFileNameFromUrl(s.header.mediaPreview) ||
                                                    cleanFileNameFromUrl(s.header.example) ||
                                                    "Document.pdf";
                                                const ext = filename.split('.').pop()?.toLowerCase() || "pdf";
                                                const fileConfigs: Record<string, { bg: string, label: string }> = {
                                                    pdf: { bg: "bg-[#EF4444]", label: "PDF" },
                                                    docx: { bg: "bg-[#2B7AD9]", label: "DOCX" },
                                                    doc: { bg: "bg-[#2B7AD9]", label: "DOCX" },
                                                    csv: { bg: "bg-[#1B9C5E]", label: "CSV" },
                                                    xlsx: { bg: "bg-[#1B9C5E]", label: "XLSX" },
                                                    xls: { bg: "bg-[#1B9C5E]", label: "XLS" },
                                                    zip: { bg: "bg-[#6B7280]", label: "ZIP" },
                                                    txt: { bg: "bg-[#6B7280]", label: "TXT" },
                                                };
                                                const config = fileConfigs[ext] || { bg: "bg-gray-500", label: ext.toUpperCase() };

                                                const sizeLabel = s.header.fileSize;
                                                const pageCount = s.header.pageCount || 1;

                                                return (
                                                    <div className="relative m-0.5 rounded-sm overflow-hidden bg-[#F0F2F5] dark:bg-[#111B21] border border-black/10">
                                                        {/* Mini Document "Scan" Placeholder */}
                                                        <div className="p-3 flex items-center gap-3">
                                                            <div className={cn("w-10 h-10 rounded-sm flex flex-col items-center justify-center text-white shrink-0 shadow-sm", config.bg)}>
                                                                <span className="text-[7px] font-black uppercase tracking-tighter leading-none mb-0.5">{config.label}</span>
                                                                <File size={16} color="white" strokeWidth={2.5} />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-[14px] font-bold text-gray-900 dark:text-gray-100 leading-tight" title={filename}>
                                                                    {truncateFileName(filename, 25)}
                                                                </p>
                                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mt-1">
                                                                    {pageCount} page{pageCount > 1 ? "s" : ""}{sizeLabel ? ` • ${sizeLabel}` : ""} • {config.label}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}



                                            {/* Content Padding Wrapper */}
                                            <div className="px-3 py-2 relative">
                                                {/* Header Text (if not already used as accent) */}
                                                {s.header.type === "TEXT" && headerTextWithValues && !headerTextWithValues.includes("Confirmed") && (
                                                    <div className="font-bold text-gray-900 dark:text-white text-sm mb-1 leading-tight break-all">
                                                        {render(headerTextWithValues)}
                                                    </div>
                                                )}

                                                {/* Body Text */}
                                                <div className="text-xs text-gray-800 dark:text-white/95 leading-[1.35] pb-1 wrap-break-word">
                                                    {s.category === "AUTHENTICATION"
                                                        ? render(replaceVariablesWithExamples(s.body.text || getAuthBodyText(s.language, s.footer.addSecurityRecommendation || false), "positional", s.body.examples || []))
                                                        : render(bodyTextWithValues)}
                                                </div>

                                                {/* Footer Text */}
                                                {s.footer.text && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-1 italic wrap-break-word">
                                                        {s.footer.text}
                                                    </div>
                                                )}
                                                <div className="mt-2">
                                                    {/* Meta Info */}
                                                    <div className="absolute bottom-1 right-2 flex items-center gap-1 opacity-60">
                                                        <span className="text-[10px] text-gray-400 dark:text-gray-400 font-medium uppercase">{time.split(' ')[0]} {time.split(' ')[1]}</span>
                                                    </div>
                                                </div>

                                                
                                                {s.footer.codeExpirationMinutes && (
                                                    <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                                                        Expiry in {s.footer.codeExpirationMinutes} minute
                                                        {Number(s.footer.codeExpirationMinutes) > 1 ? "s" : ""}
                                                    </div>
                                                )}

                                            </div>


                                            {/* Call to Action Buttons */}  
                                            <div >
                                                {/* AUTH Copy Code */}
                                                {s.category === "AUTHENTICATION" && !s.buttons.some(b => b.type === "otp" || b.otpType === "copy_code") && (
                                                    <div className="px-4 py-3 flex items-center justify-center gap-2 text-[#00a884] text-xs">
                                                        <Copy size={14} />
                                                        <span>Copy code</span>
                                                    </div>
                                                )}

                                                {/* Visible buttons */}
                                                {visibleButtons.map((btn, i) => (
                                                    <div
                                                        key={i}
                                                        className="px-4 py-2 flex items-center justify-center gap-2 text-[#00a884] text-xs border-t border-black/5 dark:border-white/5 divide-y divide-black/5"
                                                    >
                                                        {btn.type === "url" && <SquareArrowOutUpRight size={14} />}
                                                        {btn.type === 'quick_reply' && <Reply size={14} />}
                                                        {(btn.type === 'voice_call' || btn.type === 'phone_number') && <PhoneIcon size={14} />}
                                                        {(btn.type === 'otp') && <Copy size={14} />}
                                                        <span>
                                                            {btn.type === "copy_code"
                                                                ? <span className="flex items-center gap-2">
                                                                    <Copy size={14} /> Copy offer code
                                                                </span>
                                                                : btn.type === "otp" || btn.otpType
                                                                    ? "Copy code"
                                                                    : btn.type === "voice_call"
                                                                        ? (btn.voiceCallDisplayText || btn.text || "Call on WhatsApp")
                                                                        : btn.text || "Click Here"}
                                                        </span>
                                                    </div>
                                                ))}

                                                {/* See all options */}
                                                {hasMore && (
                                                    <div
                                                        onClick={() => setShowCtaPopup(true)}
                                                        className="px-4 py-2 flex items-center justify-center gap-2 text-[#00a884] text-xs border-t border-black/5 dark:border-white/5 divide-y divide-black/5 cursor-pointer"
                                                    >
                                                        <List size={14} />
                                                        <span>See all options</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>


                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Bottom Input Area - Matches Latest Screenshot */}
                        <div className="bg-transparent px-2 py-3 pb-8 flex items-center gap-2">
                            <div className="flex-1 bg-white dark:bg-[#2A3942] rounded-full px-3 py-2.5 flex items-center gap-3 shadow-md">
                                <Smile size={16} className=" text-gray-500 dark:text-white/40" />
                                <div className="flex-1 text-gray-500 dark:text-white/20 text-sm">Type a message</div>
                                <Paperclip size={14} className=" text-gray-500 dark:text-white/40 -rotate-45" />
                                <Camera size={16} className=" text-gray-500 dark:text-white/40" />
                            </div>
                            <div className="w-10 h-10 rounded-full bg-[#00A884] flex items-center justify-center shadow-lg active:scale-90 transition-transform cursor-pointer">
                                <Mic size={16} className=" text-white" />
                            </div>
                        </div>

                        {/* Navigation Bar Home Indicator */}
                        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-900/10 dark:bg-white/10 rounded-full" />
                    </div>
                </div>
                {/* CTA Popup */}
                {showCtaPopup && (
                    <div className="absolute inset-0 z-50 flex items-end justify-center rounded-3xl">

                        {/* Overlay */}
                        <div
                            className="absolute inset-0 bg-black/50 rounded-3xl"
                            onClick={() => setShowCtaPopup(false)}
                        />

                        {/* Bottom Sheet */}
                        <div className="relative bg-gray-100 dark:bg-gray-900 rounded-3xl w-full max-w-md max-h-[60vh] overflow-y-auto animate-in slide-in-from-bottom duration-300 ">

                            {s.buttons.map((btn, i) => (
                                <div
                                    key={i}
                                    className="px-4 py-3 flex items-center justify-center gap-2 text-[#00a884] text-xs border-t border-black/5 dark:border-white/5 cursor-pointer "
                                >
                                    {btn.type === "url" && <SquareArrowOutUpRight size={14} />}
                                    {(btn.type === "copy_code" || btn.type === "otp") && <Copy size={14} />}
                                    {btn.type === 'quick_reply' && <Reply size={14} />}
                                    {(btn.type === 'voice_call' || btn.type === 'phone_number') && <PhoneIcon size={14} />}
                                    <span>
                                        {btn.type === "copy_code"
                                            ? "Copy offer code"
                                            : btn.type === "otp" || btn.otpType
                                                ? "Copy code"
                                                : btn.type === "voice_call"
                                                    ? (btn.voiceCallDisplayText || btn.text || "Call on WhatsApp")
                                                    : btn.text || "Click Here"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {/* Reflection Overlay */}
            <div className="absolute inset-x-4 inset-y-8 rounded-3xl pointer-events-none bg-linear-to-tr from-white/10 via-transparent to-white/5 opacity-30 z-50 overflow-hidden" />

        </div>
    );
};

export interface WhatsAppPreviewProps {
    values?: any; // Accepting 'any' to handle diverse models via normalization
    message?: string;
    mediaUrl?: string;
    fileName?: string;
}

const WhatsAppPreview: FC<WhatsAppPreviewProps> = ({ values: propValues, message, mediaUrl, fileName }) => {
    // // Attempt to get context from CRM model
    // const crmContext = useFormikContext<FormValues>();

    // Normalize whatever we have
    const values = normalizeValues(propValues);

    if (!values) {
        // Show the full phone frame with an empty chat area
        const emptyValues: FormValues = {
            templateName: '',
            category: 'MARKETING',
            language: 'en_US',
            variableType: 'positional',
            header: { type: 'TEXT', text: '', mediaExample: '', mediaPreview: '', example: '', fileName: '' },
            body: { text: '', examples: [] },
            footer: { text: '' },
            buttons: [],
        };
        return <PhonePreview s={emptyValues} />;
    }

    // Apply overrides for campaign/direct use
    const finalValues = {
        ...values,
        body: {
            ...values.body,
            text: message || values.body.text
        },
        header: {
            ...values.header,
            mediaPreview: mediaUrl || values.header.mediaPreview,
            fileName: fileName || values.header.fileName
        }
    };

    return (
        <PhonePreview s={finalValues} />
    )
}

export default WhatsAppPreview
